// Node modules
import { Emitter } from "@quest/pixi-bridge";
import { SlotEventConstants } from "@quest/slot-core";
import { dispatcher, Container, Linear, SpineBase, delayedCall, killDelayedCall, ingenuity, Label } from "@quest/base-core";

// Class modules
import { YouWonFeatureView } from "./YouWonFeatureView";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { GameParserModel } from "../Platform/GameParserModel";
import { SoundConstantGame } from "../Constants/SoundConstantGame";
import { MeterBehavior, SlotConstantsBehavior, SlotEventConstantsBehavior, SoundConstant } from "@quest/murka-games-behavior-panther";
import { gameConstants } from "../Constants/gameConstants";
import { ITween } from "@quest/adapter-interface";

export class YouWonFeatureController {
  // Class variables
  private view: YouWonFeatureView;
  private youWonOutroContainer: Container;
  private youWonOutroSpine: SpineBase;
  private youWonOutroPortraitSpine: SpineBase;
  private youWonOutroDesktopSpineContainer: Container;
  private youWonOutroPortraitSpineContainer: Container;
  private youWonOutroWinAmount: MeterBehavior;
  private outroPopupBG: any;
  private winAmountCoinContainer: Container;
  // Emitter variable
  protected coinExplodeEmitter: Emitter;
  protected coinShowerFountainEmitter: Emitter;
  private continueText: Label;
  private transitionAnim: SpineBase;
  private winAmountMtrContainer: Container;
  private zoomInOutTweenArray: ITween[] = [];
  // Class constructor
  constructor(view: YouWonFeatureView) {
    this.view = view;
    this.subscribeEvents();
    this.initializeElements();
  }

  /**
   * Method to initialize class UI elements
   */
  private initializeElements(): void {
    this.outroPopupBG = this.view.getShapeById("outroWonPopupBG");
    this.youWonOutroContainer = this.view.getContainerByID("youWonOutroContainer");
    this.youWonOutroDesktopSpineContainer = this.view.getContainerByID("youWonOutroSpineDesktopContainer");
    this.youWonOutroSpine = this.youWonOutroDesktopSpineContainer.children[0];
    this.youWonOutroWinAmount = this.view.getMeterById("youWonOutroWinAmount") as MeterBehavior;
    if (!ingenuity.deviceEnv.isDesktop) {
      this.youWonOutroPortraitSpineContainer = this.view.getContainerByID("youWonOutroSpinePortraitContainer");
      this.youWonOutroPortraitSpine = this.youWonOutroPortraitSpineContainer.children[0];
    }
    this.transitionAnim = this.view.getSpineById("Transition_effect");
    this.continueText = this.view.getLabelById("continueText") as Label;
    this.winAmountCoinContainer = this.view.getContainerByID("winAmountCoinContainer");
    this.outroPopupBG.x = -890;
    this.outroPopupBG.y = -170;
    this.winAmountMtrContainer = this.view.getContainerByID("winAmountMtrContainer");
  }

  /** Method to subscribe events */
  private subscribeEvents(): void {
    this.unSubscribeEvents();
    dispatcher.on(eventDispatchers.SHOW_YOU_WON_OUTRO, this.onShowYouWonOutro, this);
  }

  /**
   * Method to un-subscribe events
   */
  private unSubscribeEvents(): void {
    dispatcher.off(eventDispatchers.SHOW_YOU_WON_OUTRO, this.onShowYouWonOutro, this);
  }

  /**
   * you outro presentaiton starts from here
   */
  private onShowYouWonOutro(): void {
    dispatcher.fireEvent(SlotEventConstantsBehavior.HIDE_BASE_GAME_WIN_METER);
    this.view.showView();
    this.showYouWonOutroView();
  }

  /**
   * This method show the you won outro view and play animation.
   */
  private showYouWonOutroView(): void {
    (ingenuity.soundManager as any).playSound(SoundConstantGame.BONUS_OUTRO_SOUND, 1, false, SlotConstantsBehavior.SOUND_LIST_POSTLOAD);
    this.youWonOutroContainer.visible = true;
    if (ingenuity.deviceEnv.isDesktop) {
      this.playYouWonAnim();
    } else {
      this.playYouWonAnim();
      this.playYouWonPortraitStartSpine();
    }
  }

  /**
   * This method play the you won animation
   */
  private playYouWonAnim(): void {
    this.youWonOutroSpine.visible = true;
    dispatcher.fireEvent(eventDispatchers.SHOW_YOU_WON_BG, { action: "in" });
    this.youWonOutroSpine.playAnim("in", false);
    delayedCall('startTickup',SlotConstantsBehavior.DURATION_500,()=>{
      killDelayedCall('startTickup')
        this.startYouWonOutroWinAmountTickup();
    })
    this.youWonOutroSpine.onComplete.addOnce(() => {
      this.onYouWonAnimComplete();
    }, this);
  }

  /**
   * This method play portrait you won animation
   */
  private playYouWonPortraitStartSpine(): void {
    this.youWonOutroPortraitSpine.visible = true;
    this.youWonOutroPortraitSpine.playAnim("in");
    this.youWonOutroPortraitSpine.onComplete.addOnce(() => {
      this.youWonOutroPortraitSpine.playAnim("loop", true);
    }, this);
  }

  /**
   * This method play the you won outro loop animation after you won anim complete
   */
  private onYouWonAnimComplete(): void {
    this.playYouOutroLoopAnim();
  }

  /**
   * Method to play you won loop animation
   */
  private playYouOutroLoopAnim(): void {
    dispatcher.fireEvent(eventDispatchers.SHOW_YOU_WON_BG, { action: "loop" });
    this.youWonOutroSpine.playAnim("loop", true);
  }

  /**
   * Method to show you won outro win amount tickup
   */
  private startYouWonOutroWinAmountTickup(): void {
    //this.tweenOutroAmountContainer();
    const totalWin: number = (ingenuity.parserModel as GameParserModel).getHoldAndRespinTotalWin();
    this.youWonOutroWinAmount.setValue(0);
    this.winAmountCoinContainer.visible = true;
    this.youWonOutroWinAmount.visible = true;
    this.youWonOutroWinAmount.alpha = 1;
    this.tweenOutroAmountContainer(this.winAmountMtrContainer);
    this.tweenOutroAmountContainer(this.winAmountCoinContainer);
    this.youWonOutroWinAmount && this.youWonOutroWinAmount.startTick(totalWin, true, false, 1, true, this.showContinueButton.bind(this));
  }

  // /**
  //  * tween amount container
  //  */
  private tweenOutroAmountContainer(tweenContainer: Container): void {
    this.zoomInOutTweenArray.push(
      ingenuity.currentGame.add.tween(tweenContainer.scale).to({ x: 1.1, y: 1.1 }, SlotConstantsBehavior.DELAY_FOR_NEXT_WIN_PRESENTATION, Linear.None, true, 0, -1, true)
    );
  }

  /**to show continue button */
  private showContinueButton(): void {
    this.continueText.visible = true;
    this.onStageClick();
  }

  /**
   * Method to play you won outro animation
   */
  private playYouWonOutroAnim(): void {
    if (ingenuity.deviceEnv.isDesktop) {
      dispatcher.fireEvent(eventDispatchers.SHOW_YOU_WON_BG, { action: "out" });
      this.youWonOutroSpine.playAnim("out", false);
      this.winAmountCoinContainer.visible = false;
    } else {
      dispatcher.fireEvent(eventDispatchers.SHOW_YOU_WON_BG, { action: "out" });
      this.youWonOutroSpine.playAnim("out", false);
      this.youWonOutroPortraitSpine.playAnim("out", false);
      this.youWonOutroPortraitSpine.onComplete.addOnce(() => {
        this.youWonOutroPortraitSpine.visible = false;
        this.winAmountCoinContainer.visible = false;
      }, this);
    }
    this.playTransitionAnim();
    dispatcher.fireEvent(eventDispatchers.HIDE_BONUS_REEL);
    this.youWonOutroSpine.onComplete.add(() => {
      delayedCall(
        "outroHideCompleter",
        SlotConstantsBehavior.DURATION_1000,
        () => {
          killDelayedCall("outroHideCompleter");
          this.onYouWonOutroAnimComplete();
        },
        this
      );
    }, this);
  }

  /**
   * Method to play win amount fade out tween
   */
  private playYouWonAmountFadeOutTween(): void {
    this.youWonOutroWinAmount.visible = false;
    this.youWonOutroWinAmount.alpha = 0;
  }

  /**
   *  method to hide you won outro
   */
  private onHideYouWonOutro(): void {
    dispatcher.fireEvent(eventDispatchers.HIDE_HOLD_AND_RESPIN_BACKGROUND);
    delayedCall('hideWonAmount',SlotConstantsBehavior.DURATION_200,()=>{
      killDelayedCall('hideWonAmount');
    this.playYouWonAmountFadeOutTween();
    })
    this.playYouWonOutroAnim();
    this.fadeSpinTween();
  }

  /**
   * function to play transition animation
   * freegame to basegame, basegame to freegame
   */
  private playTransitionAnim() {
    (ingenuity.soundManager as any).playSound(
      SoundConstantGame.TRANSITION_SCREEN_SOUND,
      1,
      false,
      SlotConstantsBehavior.SOUND_LIST_POSTLOAD
    );
    this.transitionAnim.visible = true;
    this.transitionAnim.playAnim("transition", true);
    this.transitionAnim.onComplete.addOnce(() => {
      this.transitionAnim.visible = false;
    });
  }

  /**
   * Method to hide you won feature
   */
  private onYouWonOutroAnimComplete(): void {
    this.youWonOutroContainer.visible = false;
    this.youWonOutroSpine.visible = false;
    this.view.hideView();
    dispatcher.fireEvent(eventDispatchers.SHOW_NEXT_FEATURE_PRESENTATION);
  }
  /**
   * This method fade the gredient background tween
   */
  private fadeSpinTween(): void {
    const fadeGredientBgTween = ingenuity.currentGame.add.tween(this.youWonOutroContainer);
    fadeGredientBgTween.to({ alpha: 0 }, 1000, Linear.None, true);
    fadeGredientBgTween.onComplete.add(() => {
      ingenuity.currentGame.tweens.remove(fadeGredientBgTween);
      this.stopYouWonOutroMtrTween();
    }, this);
  }

  // Add to stop you won outro mtr tween
  private stopYouWonOutroMtrTween(): void {
    this.zoomInOutTweenArray.forEach((zoomInOutTween: ITween) => {
      zoomInOutTween.stop();
      ingenuity.currentGame.tweens.remove(zoomInOutTween);
    }, this);
    this.zoomInOutTweenArray = [];
  }

  /**to handle continue button pressup */
  private onContinuePressUP(): void {
    ((ingenuity.parserModel as GameParserModel).getPromotionalFreeGame()) && dispatcher.fireEvent(SlotEventConstantsBehavior.SHOW_HIDE_PROMOTIONAL_FREE_PLAY, true);
    SlotConstantsBehavior.FREEGAME_RETURN_SOUND = true;
    dispatcher.fireEvent(SlotEventConstantsBehavior.PLAY_GAME_SOUND, { id: SoundConstant.COMMON_CLICK_SOUND, vol: 1, loop: false });
    this.resetElementCounts();
    (ingenuity.soundManager as any).stop(SoundConstantGame.BONUS_OUTRO_SOUND, SlotConstantsBehavior.SOUND_LIST_POSTLOAD);
    dispatcher.fireEvent(SlotEventConstants.SHOW_BASEGAME_VIEW);
    (ingenuity.parserModel as GameParserModel).setHoldAndRespinStatus(false);
    this.onHideYouWonOutro();
  }

  /**
   * reset element count after hold 'n' respin
   */
  private resetElementCounts() {
    let triggerType = (ingenuity.parserModel as GameParserModel).getHoldAndRespinType();
    if (triggerType.includes(gameConstants.SKULL_COLLECT_ALL)) {
      (ingenuity.parserModel as GameParserModel).setGreenElementCount(0);
    }
    if (triggerType.includes(gameConstants.SKULL_BIG_PRIZE)) {
      (ingenuity.parserModel as GameParserModel).setBlueElementCount(0);
    }
    if (triggerType.includes(gameConstants.SKULL_MULTIPLIER)) {
      (ingenuity.parserModel as GameParserModel).setRedElementCount(0);
    }
  }

  onStageClick() {
    this.outroPopupBG.interactive = true;
    this.outroPopupBG.inputEnabled = true;
    this.outroPopupBG &&
      this.outroPopupBG.events.onInputUp.add((e: any) => {
        this.onContinuePressUP();
        this.offStageClick();
      }, this);
  }
  offStageClick() {
    this.outroPopupBG.interactive = false;
    this.outroPopupBG.inputEnabled = false;
    this.outroPopupBG && this.outroPopupBG.events.onInputUp.dispose();
  }
}
